import type { ReactNode } from "react"

import { MotionDiv, MotionList, MotionSection, blurIn, fadeInLeft } from "@/components/motion"
import { Badge } from "@/components/ui/badge"
import { Card } from "@/components/ui/card"
import { cn } from "@/lib/utils"

export type FeatureBentoItem = {
  title: string
  description: string
  icon?: ReactNode
  meta?: string
  className?: string
}

export type FeatureBentoProps = {
  eyebrow?: string
  title: string
  description?: string
  items: FeatureBentoItem[]
  className?: string
}

/** Premium bento feature section with mixed card scale and richer hierarchy. */
export function FeatureBento({ eyebrow, title, description, items, className }: FeatureBentoProps) {
  return (
    <MotionSection className={cn("relative overflow-hidden bg-background py-24 sm:py-32", className)}>
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-border to-transparent" />
      <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
        <MotionDiv variants={fadeInLeft} className="max-w-3xl">
          {eyebrow && <Badge variant="outline" className="mb-5 rounded-full px-3 py-1 text-primary">{eyebrow}</Badge>}
          <h2 className="font-heading text-4xl font-semibold tracking-tight text-foreground sm:text-5xl">
            {title}
          </h2>
          {description && <p className="mt-5 text-lg leading-8 text-muted-foreground">{description}</p>}
        </MotionDiv>

        <MotionList className="mt-14 grid gap-4 md:grid-cols-6">
          {items.map((item, index) => {
            const featured = index === 0 || index === 3
            return (
              <MotionDiv
                key={`${item.title}-${index}`}
                variants={blurIn}
                className={cn(
                  featured ? "md:col-span-3 lg:p-8" : "md:col-span-2",
                )}
              >
                <Card
                  className={cn(
                    "group relative h-full overflow-hidden rounded-panel border-border/70 bg-card p-6 shadow-[0_1px_2px_hsl(var(--foreground)/0.04),0_18px_54px_hsl(var(--foreground)/0.08)] transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_24px_80px_hsl(var(--foreground)/0.14)]",
                    item.className,
                  )}
                >
                  <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_10%,hsl(var(--primary)/0.14),transparent_28%)] opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                  <div className="relative z-10">
                    <div className="mb-8 flex items-center justify-between gap-4">
                      {item.icon ? (
                        <div className="flex size-12 items-center justify-center rounded-card bg-primary/10 text-primary ring-1 ring-primary/15">
                          {item.icon}
                        </div>
                      ) : (
                        <div className="size-12 rounded-card bg-gradient-to-br from-primary/18 to-accent ring-1 ring-primary/12" />
                      )}
                      {item.meta && <span className="text-xs font-medium uppercase tracking-[0.16em] text-muted-foreground">{item.meta}</span>}
                    </div>

                    <h3 className="font-heading text-2xl font-semibold tracking-tight text-foreground">
                      {item.title}
                    </h3>
                    <p className="mt-4 text-base leading-7 text-muted-foreground">
                      {item.description}
                    </p>
                  </div>
                </Card>
              </MotionDiv>
            )
          })}
        </MotionList>
      </div>
    </MotionSection>
  )
}
